// Client correlation ids for App Insights (flag clientCorrelationEnabled).
//
// Prod telemetry cannot attribute a refetch cascade to the click that caused
// it: ClientIP is one shared value and SessionId/UserAuthenticatedId are
// empty in AppRequests. authFetch therefore sends two headers:
//   x-km-session  one random id per tab, kept in sessionStorage so it
//                 survives reloads within the tab (a new tab = a new id);
//   x-km-click    the id of the most recent user mutation (markClick is called
//                 by the repositories when a mutation starts) - attached to
//                 every request until the click SETTLES: the repositories
//                 call settleClick when the mutation's promise settles, and
//                 the id then stays attached for CLICK_SETTLE_GRACE_MS more
//                 (the refetches an invalidate starts on settle are issued
//                 within that grace), capped at CLICK_WINDOW_MS from the
//                 click. So the PUT and the refetches it triggers share one
//                 id, while a cart sync or prefetch half a minute later does
//                 not. Measurement caveat: an unrelated request that happens
//                 to fall inside the window still gets the id - the KQL
//                 filters on Name (GetDesigns/GetDesign) for that reason.
// Pure module: no flag reads here - the flag gate sits in authFetch, so with
// the flag off nothing on the wire changes and markClick is a memory write.
//
// x-km-op (flag designPrefetchLeanEnabled, 28/9): the per-call ACTION header
// the API's list telemetry reads (ClientCorrelation.ReadOp: one value of
// 1-64 characters [A-Za-z0-9_-], anything else = "-"). Unlike the two
// headers above it is not attached by authFetch on its own: a caller asks
// for it per request (opHeaders -> authFetch's extraHeaders), and today only
// prefetchDesigns does, with the flag on.

export const KM_SESSION_HEADER = "x-km-session";
export const KM_CLICK_HEADER = "x-km-click";
export const KM_OP_HEADER = "x-km-op";
export const CLICK_WINDOW_MS = 10_000;
export const CLICK_SETTLE_GRACE_MS = 2_000;
export const SESSION_ID_STORAGE_KEY = "kmClientSessionId";
export const CLIENT_ID_LENGTH = 22;

const ID_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";
const CLIENT_ID_PATTERN = /^[A-Za-z0-9_-]{16,32}$/;
// The API's token rule (ClientCorrelation.IsValidToken): 1-64 characters of
// [A-Za-z0-9_-], no trimming.
const OP_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

let sessionId: string | undefined;
let lastClick: { id: string; at: number; until: number } | undefined;

export function isValidClientId(value: unknown): value is string {
  return typeof value === "string" && CLIENT_ID_PATTERN.test(value);
}

// Random id over [A-Za-z0-9_-] (64 symbols = 6 bits per byte, no bias).
export function newClientId(length: number = CLIENT_ID_LENGTH): string {
  const bytes = new Uint8Array(length);
  const cryptoApi =
    typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function"
      ? crypto
      : undefined;
  if (cryptoApi) {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  let id = "";
  for (let i = 0; i < length; i++) {
    id += ID_ALPHABET[bytes[i] & 63];
  }
  return id;
}

// One id per tab: read from sessionStorage when present and well-formed,
// otherwise generate and try to store it. Storage can throw (private mode,
// quota, disabled) - the id then lives for the page lifetime only.
export function getClientSessionId(): string {
  if (sessionId) return sessionId;
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
  } catch {
    stored = null;
  }
  sessionId = isValidClientId(stored) ? stored : newClientId();
  if (sessionId !== stored) {
    try {
      sessionStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
    } catch {
      // Keep the in-memory id.
    }
  }
  return sessionId;
}

// Called by the repositories when a user mutation starts (verdict, approval
// start, delete, color change, resize, upload). Returns the new click id.
export function markClick(now: number = Date.now()): string {
  const id = newClientId();
  lastClick = { id, at: now, until: now + CLICK_WINDOW_MS };
  return id;
}

// Called when the mutation's promise settles (success or failure): the id
// stays attached for CLICK_SETTLE_GRACE_MS more, never past the window. A
// later click has replaced the id already - then this is a no-op.
export function settleClick(id: string, now: number = Date.now()): void {
  if (!lastClick || lastClick.id !== id) return;
  lastClick.until = Math.min(lastClick.until, now + CLICK_SETTLE_GRACE_MS);
}

// Runs a mutation with a click id marked at the start and settled when it
// settles. Returns the mutation's own promise (same value, same rejection).
export async function withClick<T>(work: () => Promise<T>): Promise<T> {
  const id = markClick();
  try {
    return await work();
  } finally {
    settleClick(id);
  }
}

// The click id to attach right now - undefined when no click is active
// (none yet, settled + grace elapsed, window elapsed, or the clock moved
// backwards).
export function getActiveClickId(now: number = Date.now()): string | undefined {
  if (!lastClick) return undefined;
  if (now < lastClick.at || now > lastClick.until) return undefined;
  return lastClick.id;
}

export function clientCorrelationHeaders(
  now: number = Date.now()
): Record<string, string> {
  const headers: Record<string, string> = {
    [KM_SESSION_HEADER]: getClientSessionId(),
  };
  const clickId = getActiveClickId(now);
  if (clickId) headers[KM_CLICK_HEADER] = clickId;
  return headers;
}

// The caller's headers plus the correlation headers, in the caller's own
// shape (Headers instance, tuple array or plain object). Never mutates the
// input.
export function withClientCorrelationHeaders(
  headers: HeadersInit | undefined,
  now: number = Date.now()
): HeadersInit {
  const extra = clientCorrelationHeaders(now);
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    const copy = new Headers(headers);
    Object.entries(extra).forEach(([name, value]) => copy.set(name, value));
    return copy;
  }
  if (Array.isArray(headers)) {
    return [...headers, ...Object.entries(extra)];
  }
  return { ...(headers ?? {}), ...extra };
}

export function isValidOp(value: unknown): value is string {
  return typeof value === "string" && OP_PATTERN.test(value);
}

// The x-km-op header for one request: { "x-km-op": op } when op is a valid
// token, otherwise NO header at all (an empty object) - an invalid value is
// never sent, so the API never has to discard it.
export function opHeaders(op?: string): Record<string, string> {
  return isValidOp(op) ? { [KM_OP_HEADER]: op } : {};
}

// The caller's headers plus `extra`, in the caller's own shape (Headers
// instance, tuple array or plain object) - `extra` wins on a name clash.
// Never mutates the input. Same contract as withClientCorrelationHeaders.
export function withExtraHeaders(
  headers: HeadersInit | undefined,
  extra: Record<string, string>
): HeadersInit {
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    const copy = new Headers(headers);
    Object.entries(extra).forEach(([name, value]) => copy.set(name, value));
    return copy;
  }
  if (Array.isArray(headers)) {
    return [...headers, ...Object.entries(extra)];
  }
  return { ...(headers ?? {}), ...extra };
}

export function resetClientCorrelationForTests(): void {
  sessionId = undefined;
  lastClick = undefined;
}
